import React, { useEffect, useState } from "react";
import { Card, Checkbox } from "antd";

type BitLabel = {
  label: string;
  setChildren?: number[];
  unsetChildren?: number[];
};

type BitGroup = Record<string, BitLabel>; // JSON keys are strings (bit indexes)
export type BitLabels = Record<string, BitGroup>; // group title -> (bitIndex -> BitLabel)

type Props = {
  value?: bigint | number | string | null;
  onChange: (v: bigint) => void;
  labels: BitLabels;
  /** optional: how many columns for the group display */
  columns?: number;
};

const toBigInt = (v?: bigint | number | string | null): bigint => {
  if (v === undefined || v === null) return 0n;
  if (typeof v === "bigint") return v;
  // if it's a number, convert to BigInt safely
  if (typeof v === "number") return BigInt(v);
  // otherwise try parse string
  try {
    return BigInt(v.toString());
  } catch {
    return 0n;
  }
};

export const BitwiseEditor: React.FC<Props> = ({ value, onChange, labels, columns = 3 }) => {
  const [bitsValue, setBitsValue] = useState<bigint>(() => toBigInt(value));

  // keep local state in sync if prop value changes
  useEffect(() => {
    setBitsValue(toBigInt(value));
  }, [value]);

  const toggleBit = (pos: number) => {
    const mask = 1n << BigInt(pos);
    const newVal = (bitsValue & mask) !== 0n ? (bitsValue & ~mask) : (bitsValue | mask);
    setBitsValue(newVal);
    onChange(newVal);
  };

  // helper to check bit
  const isBitSet = (pos: number) => (bitsValue & (1n << BigInt(pos))) !== 0n;

  // layout helper: render groups in columns grid
  const groupStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: 12,
  };

  return (
    <Card title="Configurar Bitwise">
      {Object.entries(labels).map(([groupTitle, items]) => (
        <div key={groupTitle} style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 8 }}>{groupTitle}</h3>

          <div style={groupStyle}>
            {Object.entries(items as BitGroup).map(([bitStr, cfg]) => {
              // cfg is typed now
              const bitIndex = Number(bitStr);
              if (Number.isNaN(bitIndex)) return null;
              return (
                <div key={bitStr}>
                  <Checkbox checked={isBitSet(bitIndex)} onChange={() => toggleBit(bitIndex)}>
                    {cfg.label}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </Card>
  );
};

export default BitwiseEditor;
